import ModelBaseClass from "@quasidea/oas-client-react/lib/ModelBaseClass";
import CashCollectionSearchRequest from "../CashCollectionSearchRequest";
import ModelProxyClass from "./ModelProxyClass";
import ResultParameter from "../ResultParameter";


/**
 * @class CashCollectionSearchRequestBase
 * @extends ModelBaseClass
 * @property {number} linkedToCashCollectionId (int64) to view only the Collections that 'linked to' another collection (e.g. a group of VgtDrops, or a BvDrop/RtAudit that are linked together)
 * @property {number} currentlyWithCashAccountId (int64) to view only the Collections that are 'currently with' a vault (not yet completely processed) or with a person
 * @property {number} fromLocationId (int64) to view only the Collections that were collected from a location
 * @property {number} collectedByPersonId (int64) history of collections that were collected by a given person
 * @property {Date} filterByDateRangeStart (date only) filter by the date of the original collection
 * @property {Date} filterByDateRangeEnd (date only) filter by the date of the original collection
 * @property {ResultParameter} resultParameter [DateCollected]
 */
class CashCollectionSearchRequestBase extends ModelBaseClass {
	/**
	 * to view only the Collections that 'linked to' another collection (e.g. a group of VgtDrops, or a BvDrop/RtAudit that are linked together)
	 * @type {number} linkedToCashCollectionId (int64)
	 */
	linkedToCashCollectionId;
	/**
	 * to view only the Collections that are 'currently with' a vault (not yet completely processed) or with a person
	 * @type {number} currentlyWithCashAccountId (int64)
	 */
	currentlyWithCashAccountId;
	/**
	 * to view only the Collections that were collected from a location
	 * @type {number} fromLocationId (int64)
	 */
	fromLocationId;
	/**
	 * history of collections that were collected by a given person
	 * @type {number} collectedByPersonId (int64)
	 */
	collectedByPersonId;
	/**
	 * filter by the date of the original collection
	 * @type {Date} filterByDateRangeStart (date only)
	 */
	filterByDateRangeStart;
	/**
	 * filter by the date of the original collection
	 * @type {Date} filterByDateRangeEnd (date only)
	 */
	filterByDateRangeEnd;
	/**
	 * [DateCollected]
	 * @type {ResultParameter} resultParameter
	 */
	resultParameter;

	/**
	 * Instantiates a new instance of CashCollectionSearchRequest based on the generic object being passed in (typically from a JSON object)
	 * @param {object} genericObject
	 * @return {CashCollectionSearchRequest}
	 */
	static create(genericObject) {
		const newCashCollectionSearchRequest = new CashCollectionSearchRequest();
		newCashCollectionSearchRequest.instantiate(_modelDefinition, genericObject, ModelProxyClass.createByClassName);
		return newCashCollectionSearchRequest;
	}

	/**
	 * Instantiates a new array of CashCollectionSearchRequest based on the generic array being passed in (typically from a JSON array)
	 * @param {[object]} genericArray
	 * @return {[CashCollectionSearchRequest]}
	 */
	static createArray(genericArray) {
		if (genericArray === null) {
			return null;
		}

		const newCashCollectionSearchRequestArray = [];
		genericArray.forEach(genericObject => {
			newCashCollectionSearchRequestArray.push(CashCollectionSearchRequest.create(genericObject));
		});
		return newCashCollectionSearchRequestArray;
	}
}

/**
 * @type {string} OrderByDateCollected
 */
CashCollectionSearchRequestBase.OrderByDateCollected = 'datecollected';

const _modelDefinition = [
	ModelBaseClass.createModelProperty('linkedToCashCollectionId', 'integer'),
	ModelBaseClass.createModelProperty('currentlyWithCashAccountId', 'integer'),
	ModelBaseClass.createModelProperty('fromLocationId', 'integer'),
	ModelBaseClass.createModelProperty('collectedByPersonId', 'integer'),
	ModelBaseClass.createModelProperty('filterByDateRangeStart', 'datetime'),
	ModelBaseClass.createModelProperty('filterByDateRangeEnd', 'datetime'),
	ModelBaseClass.createModelProperty('resultParameter', 'ResultParameter'),
];

export default CashCollectionSearchRequestBase;
